/* global activeView */


// Import JS Modules
import { builderDispatch, WebUI, PrismUI } from '../../_utils/ui';
import TemplateCache from '../templates/template-cache';
import TemplateData from '../templates/template-data';
import TemplateStore from '../templates/template-store';
import { getCurrentSection } from '../trays/tray-helpers';
import Shuffle from '../prism/activeview-manipulations/shuffle';
import { usePrismItemStore } from 'stores/usePrismItemStore';
import { useAllSlotsStore } from 'stores/useAllSlotsStore';

// --------------------------------------------------------------------

builderDispatch.register((payload) => {
  switch (payload.type) {
    case WebUI.TEMPLATE_CHANGED:
      handleTemplateChanged();
      break;
    case WebUI.PAGES_CHANGED:
      handlePagesChanged();
      break;
  }
});


export function handleSelectTemplate({ templateID, shuffle }) {
  if (activeView.customPage) {
    return;
  }

  if (window.activeView.template && activeView.template.id === templateID && !shuffle) {
    return;
  }

  if (shuffle) {
    const nextLayout = Shuffle.nextLayout;
    templateID = TemplateStore.getDesignEquivalent(nextLayout.id, nextLayout.id);
  }

  TemplateCache.get(templateID, (templateData) => {
    // transform page into
    if (TemplateCache.templatesMatch(activeView.template, templateData)) {
      activeView.suppressSave('change-template-set-data', (view) => {
        view.deleteAllOverrideImageSlots();
        view.repositionTextSlots(templateData);
        const data = view.getData(false);
        data.templateID = templateData.id;
        view.setData(data);
      });

    } else {
      // XXX: image orientation, colour, etc.  and user text
      activeView.suppressSave('change-template-set-images', (view) => {
        view.deleteAllOverrideImageSlots();
        const [previousImages, previousTexts] = [view.getImages(), view.getTexts()];

        // Snapshot deleted fixed SVG text slot states before the template changes.
        // getTexts() only captures legacy isText slots, so deleted SVGText slots
        // would otherwise be lost when the new template loads fresh slots.
        const deletedSlotSnapshot = view.slots
          .filter(s => s.isSvgText && s.isDeletedFixedSlot)
          .map(s => ({ id: s.id, deletedPlaceholderSvgUrl: s.deletedPlaceholderSvgUrl }));
        usePrismItemStore.getState().saveDeletedFixedSlots(
          view.pageIndex,
          view.template.id,
          deletedSlotSnapshot,
        );

        view.loadTemplate(templateData);
        view.setImages(previousImages);
        view.setTexts(previousTexts);

        // After loading the new template, restore any slots that were marked
        // deleted the last time this layout was active on this page.
        // Setting _restoredFromSave = true prevents the loadTemplate async IIFE
        // from overwriting the restored state with template defaults.
        const previouslyDeleted = usePrismItemStore.getState().getDeletedFixedSlots(
          view.pageIndex,
          templateData.id,
        );
        previouslyDeleted.forEach(({ id, deletedPlaceholderSvgUrl }) => {
          const slot = view.slots.find(s => s.isSvgText && s.id === id);
          if (!slot) return;
          slot.isDeletedFixedSlot = true;
          slot.string = '';
          slot.deletedPlaceholderSvgUrl = deletedPlaceholderSvgUrl;
          slot._restoredFromSave = true;
          slot.loadPlaceholderImg();
        });
      });
    }
    activeView.do_save();

    (() => {
      const tmr = setInterval(() => {
        // hang on a moment until getCurrentSection returns a usuable value
        const activeSection = getCurrentSection();
        if (typeof activeSection !== 'undefined') {
          clearInterval(tmr);

          builderDispatch.dispatch({
            type: WebUI.TEMPLATE_CHANGED,
            template: templateData,
          });

          // Update total slots after template selection
          useAllSlotsStore.getState().updateTotalSlots();
        }
      }, 10);
    })();
  }, (error) => {
    // report error
    console.error(error);
  });
}

function handleTemplateChanged() {
  const prismItem = usePrismItemStore.getState().prismItem;

  if ((wkData.product.type !== 'wmcard' && wkData.product.type !== 'businesscard') || typeof prismItem === 'undefined' || typeof window.activeView === 'undefined') {
    return;
  }

  const promises = [];

  // start loading
  promises.push(
    builderDispatch.delayedDispatch({
      type: PrismUI.MODAL_MESSAGE,
    }),
  );

  for (let i = 0; i < prismItem.pageCount; i++) {
    if (activeView.pageIndex == i) {
      continue;
    }

    promises.push(new Promise((resolve) => {
      usePrismItemStore.getState().pageWithTemplate(i).then(({ page, template }) => {
        const orientations = ['is-portrait', 'is-landscape', 'is-square'];
        const orientation = orientations.find((orientation) => activeView.template.tags.includes(orientation) && !template.tags.includes(orientation));

        if (!orientation) {
          resolve();
          return;
        }

        const { id: newTemplateID } = (() => {
          const availableTemplates = TemplateStore.templatesFiltered({
            section: page.section,
            orientations: orientation
          });
          const availableTemplatesWithSameNumberOfPhotos = availableTemplates.filter((availableTemplate) => availableTemplate['no-photos'] === template.pso.filter((k) => k !== 'optional-bg').length);

          return availableTemplatesWithSameNumberOfPhotos[0] || availableTemplates[0] || {};
        })();

        if (typeof newTemplateID === 'undefined') {
          resolve();
          return;
        }

        TemplateCache.get(newTemplateID, (templateB) => {
          let updatedPage = {
            ...page,
            templateID: newTemplateID,
            templateId: newTemplateID,
            template: null,
          };

          updatedPage = TemplateData.copyData(page, template, updatedPage, templateB);

          usePrismItemStore.getState().savePage(page.index, updatedPage).then(() => resolve());
        });
      });
    }));
  }

  Promise.all(promises).then(() => {
    // stop loading
    builderDispatch.delayedDispatch({
      type: PrismUI.MODAL_MESSAGE,
      action: 'hide',
    });
  });
}

function handlePagesChanged() {
  const prismItem = usePrismItemStore.getState().prismItem;
  for (const index in prismItem.pages) {
    const page = prismItem.pages[index];
    TemplateCache.get(page.templateID);
  }
}
